import { useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react'
import type { Health, NodeRef, OutputState } from '../api'
import { Icon, Toggle } from '../ui'

/** state unknown / node_unreachable in the admin pages */
export const NODE_UNKNOWN = 'Node unreachable, state unknown'

/** "fra1 · eu-central" */
export const nodeText = (n: NodeRef) => (n.region ? `${n.name} · ${n.region}` : n.name)

export type Lamp = 'live' | 'busy' | 'error' | 'ready' | 'dark'

export function Tally({ state, label }: { state: Lamp; label: string }) {
  return <span className="tally" data-state={state} role="img" aria-label={label} />
}

export interface StateInfo {
  text: string
  lamp: Lamp
  busy: boolean // switch locked: only short API-side transitions; a connecting or failing output can always be switched off
}

export function outputState(s: OutputState): StateInfo {
  switch (s) {
    case 'live':
      return { text: 'On air', lamp: 'live', busy: false }
    case 'waiting':
      return { text: 'Ready, waiting for your stream', lamp: 'ready', busy: false }
    case 'pending':
    case 'starting':
      return { text: 'Connecting…', lamp: 'busy', busy: s === 'pending' }
    case 'reconnecting':
      return { text: 'Reconnecting…', lamp: 'busy', busy: false }
    case 'stopping':
      return { text: 'Stopping…', lamp: 'busy', busy: true }
    case 'failed':
      return { text: 'Error', lamp: 'error', busy: false }
    case 'off':
      return { text: 'Off', lamp: 'dark', busy: false }
    case 'stopped':
      return { text: 'Stopped', lamp: 'dark', busy: false }
    case 'unknown':
      return { text: NODE_UNKNOWN, lamp: 'dark', busy: false }
    default:
      return { text: 'No data from the server', lamp: 'dark', busy: false }
  }
}

export const healthText: Record<Health, string> = {
  good: 'Healthy',
  warning: 'Needs attention',
  bad: 'Problem',
  offline: 'Offline',
}

/**
 * The system's button (Buttons): primary = filled live (one per view), plain = outline, danger =
 * filled fail for the destructive step, quiet = ghost. Callers' utilities (min-h-[44px], w-full)
 * win over the class layer.
 */
export function Button({
  variant = 'plain',
  className = '',
  ...p
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'plain' | 'danger' | 'quiet' }) {
  const v = { primary: 'primary', plain: '', danger: 'danger', quiet: 'ghost sm' }[variant]
  // no default type: inside a form it submits, as before
  return <button {...p} className={`sx-btn ${v} ${className}`} />
}

/** The system's field (Fields): label, input with the line-strong edge, help or error below. */
export function Field({
  label,
  hint,
  error,
  ...p
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: ReactNode; error?: string }) {
  const id = p.id ?? p.name
  return (
    <div className="sx-field" data-state={error ? 'fail' : undefined}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        {...p}
        className="sx-input sans w-full text-base placeholder:text-muted focus:border-accent focus:shadow-focus focus:outline-none sm:text-sm"
      />
      {hint && <span className="help">{hint}</span>}
      {error && (
        <span className="err">
          <Icon name="state-fail" size={12} />
          {error}
        </span>
      )}
    </div>
  )
}

/** A message in a page: error = fail (icon, double edge), info = neutral. */
export function Notice({ kind = 'error', children }: { kind?: 'error' | 'info'; children: ReactNode }) {
  if (!children) return null
  return (
    <p
      role={kind === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-2 rounded-md px-3 py-2 text-sm ${kind === 'error' ? 'border-[3px] border-double border-fail/45 bg-fail/[.07] text-fail' : 'border border-line-2 text-text-soft'}`}
    >
      <Icon name={kind === 'error' ? 'state-fail' : 'state-idle'} size={16} className="mt-0.5" />
      <span className="min-w-0">{children}</span>
    </p>
  )
}

export function Panel({ title, aside, children, className = '' }: { title?: ReactNode; aside?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`rs-corners rounded-lg border border-line ${className}`}>
      {title && (
        <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
          <h2 className="text-lg font-semibold">{title}</h2>
          {aside}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  )
}

// Switch: the system's toggle; reflects the node, not the click (pending while it confirms).
export function Switch({
  on,
  busy,
  onChange,
  label,
}: {
  on: boolean
  busy: boolean
  onChange: (on: boolean) => void
  label: string
}) {
  return <Toggle checked={on} pending={busy} aria-label={label} disabled={busy} onChange={onChange} className="disabled:cursor-wait" />
}

// Secret: hidden by default, with show and copy.
export function Secret({ value, load, label }: { value?: string; load?: () => Promise<string>; label: string }) {
  const [shown, setShown] = useState(false)
  const [v, setV] = useState(value)
  const [copied, setCopied] = useState(false)
  const [err, setErr] = useState('')
  const ensure = async () => {
    if (v !== undefined) return v
    try {
      const got = (await load?.()) ?? ''
      setV(got)
      return got
    } catch (e) {
      setErr((e as Error).message)
      return ''
    }
  }
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <code
          aria-label={label}
          className="num min-w-0 flex-1 truncate rounded-md border border-line bg-bg px-3 py-2 font-mono text-xs"
        >
          {shown && v ? v : '•'.repeat(24)}
        </code>
        <Button
          onClick={async () => {
            if (!shown) await ensure()
            setShown(!shown)
          }}
        >
          {shown ? 'Hide' : 'Show'}
        </Button>
        <Button
          onClick={async () => {
            const s = await ensure()
            if (!s) return
            await navigator.clipboard?.writeText(s).catch(() => undefined)
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      {err && <p className="mt-1 text-xs text-fail">{err}</p>}
    </div>
  )
}

export function Copyable({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="flex items-center gap-2">
      <code aria-label={label} className="min-w-0 flex-1 truncate rounded-md border border-line bg-bg px-3 py-2 font-mono text-xs">
        {value}
      </code>
      <Button
        onClick={async () => {
          await navigator.clipboard?.writeText(value).catch(() => undefined)
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  )
}

export const kbps = (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(1)} Mbps` : `${Math.round(v)} kbps`)

export function duration(sinceIso?: string, now = Date.now()) {
  if (!sinceIso) return ''
  const s = Math.max(0, Math.floor((now - Date.parse(sinceIso)) / 1000))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`
}

export function ago(iso?: string) {
  if (!iso) return '—'
  const s = Math.round((Date.now() - Date.parse(iso)) / 1000)
  if (s < 60) return `${s}s ago`
  if (s < 3600) return `${Math.round(s / 60)} min ago`
  if (s < 86400) return `${Math.round(s / 3600)} h ago`
  return new Date(iso).toLocaleDateString()
}
